/**
 * Master-document section grouping.
 *
 * The Warranty Master Doc lists ~25 sections as siblings (purpose,
 * coverage_ladder, commissioning_obligations, ..., labor_coverage_purpose,
 * labor_coverage_dealer_only_24mo, ..., labor_coverage_no_waiver). Without
 * grouping, the 15 labor-coverage sections look like just-more-fine-print.
 *
 * `groupMasterDocSections` walks the section list in order and clusters
 * consecutive sections whose `key` matches a registered group prefix (e.g.
 * `labor_coverage_*`) into a single `policyGroup` block. The renderer can
 * then emit a styled banner once per cluster instead of repeating it per
 * section. Idempotent + key-stable so adding/reordering doesn't break the
 * UI.
 *
 * Adding a new policy group: append an entry to POLICY_GROUPS below.
 */
export const POLICY_GROUPS = [
  {
    id: "labor_coverage",
    keyPrefix: "labor_coverage_",
    titleKey: "wmd.labor_coverage_policy",
    titleFallback: "Labor Coverage Policy",
    summaryKey: "wmd.labor_coverage_policy_summary",
    summaryFallback:
      "When warranty labor is covered, who can perform it, the 24-month dealer-only window, " +
      "and the dealer-account-credit reimbursement model. (Master Doc v1.4.0+)",
    accent: "violet",
  },
];

const PREFIX_BY_ID = Object.fromEntries(POLICY_GROUPS.map((g) => [g.id, g]));

/**
 * Detect which policy group a section belongs to (or null).
 */
export function policyGroupForSection(section) {
  if (!section || !section.key) return null;
  for (const g of POLICY_GROUPS) {
    if (section.key.startsWith(g.keyPrefix)) return g;
  }
  return null;
}

/**
 * Group a flat sections array into an ordered list of blocks. Each block
 * is either a single ungrouped section or a cluster under a policy
 * group. Output preserves source order so a section moved between groups
 * keeps its narrative position.
 *
 * Output shape:
 *   [{ kind: "section",     section: <section> },
 *    { kind: "policy_group", group: <POLICY_GROUPS entry>,
 *      sections: [<section>, ...] }]
 */
export function groupMasterDocSections(sections) {
  const out = [];
  let current = null;
  for (const s of sections || []) {
    const g = policyGroupForSection(s);
    if (!g) {
      current = null;
      out.push({ kind: "section", section: s });
      continue;
    }
    if (current && current.group.id === g.id) {
      current.sections.push(s);
      continue;
    }
    current = { kind: "policy_group", group: g, sections: [s] };
    out.push(current);
  }
  return out;
}

/**
 * Count sections per policy group — used by the doc heading badges.
 */
export function policyGroupCounts(sections) {
  const counts = {};
  for (const s of sections || []) {
    const g = policyGroupForSection(s);
    if (g) counts[g.id] = (counts[g.id] || 0) + 1;
  }
  return counts;
}

export function getPolicyGroupById(id) {
  return PREFIX_BY_ID[id] || null;
}
